import React, { useEffect, useRef, useCallback } from 'react';
import Settings from '../Settings/Settings';
import styles from './WelcomeBlock.module.css';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAuth } from '../../context/AuthContext';
import { Capacitor } from '@capacitor/core';
import { SITE_CONFIG } from '../../config/siteConfig';

// helpers
const normalizeLang = (lng) => {
  if (lng === 'ua') return 'uk';
  if (lng === 'ru' || lng === 'uk' || lng === 'en' || lng === 'de') return lng;
  return 'ru';
};

function beaconJSON(url, data) {
  const payload = JSON.stringify(data);

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'text/plain' });
    return navigator.sendBeacon(url, blob);
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
    credentials: 'include',
  }).catch(() => {});

  return true;
}

// analytics: time on welcome screen
function useWelcomeTimeAnalytics({ trackWelcome, userId }) {
  const accMsRef = useRef(0);
  const lastVisibleAtRef = useRef(null);
  const sentRef = useRef(false);

  const now = () => performance.now();

  const flush = useCallback(
    (completed = false) => {
      if (!trackWelcome || !userId || sentRef.current) return;

      if (lastVisibleAtRef.current != null) {
        accMsRef.current += Math.max(0, now() - lastVisibleAtRef.current);
        lastVisibleAtRef.current = null;
      }

      sentRef.current = true;

      beaconJSON('/api/analytics/welcome/flush', {
        temp_userid: userId,
        welcome_steps_total: 1,
        welcome_steps_seen: 1,
        welcome_time_ms: Math.round(accMsRef.current),
        completed: !!completed,
      });
    },
    [trackWelcome, userId]
  );

  useEffect(() => {
    if (!trackWelcome || !userId) return;

    const updateVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (lastVisibleAtRef.current == null) {
          lastVisibleAtRef.current = now();
        }
      } else if (lastVisibleAtRef.current != null) {
        accMsRef.current += Math.max(0, now() - lastVisibleAtRef.current);
        lastVisibleAtRef.current = null;
      }
    };

    updateVisibility();

    const onPageHide = () => flush(false);

    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      flush(false);
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [trackWelcome, userId, flush]);

  return { flush };
}

const WELCOME_CONTENT = {
  ru: {
    title: `${SITE_CONFIG.brandName} — современный способ выучить язык быстрее:`,
    items: [
      'Без тысяч $$$ на репетиторов',
      'Игровой формат вместо зубрёжки',
      '3000 слов = до 95–98% понимания речи',
      'Начни говорить уже в процессе обучения',
      'От A1 до B2+ за ~6 месяцев',
    ],
  },
  uk: {
    title: `${SITE_CONFIG.brandName} — сучасний спосіб вивчити мову швидше:`,
    items: [
      'Без тисяч $$$ на репетиторів',
      'Ігровий формат замість зубріння',
      '3000 слів = до 95–98% розуміння мовлення',
      'Почни говорити вже в процесі навчання',
      'Від A1 до B2+ приблизно за 6 місяців',
    ],
  },
  en: {
    title: `${SITE_CONFIG.brandName} — a modern way to learn a language faster:`,
    items: [
      'No thousands spent on tutors',
      'Game-like format instead of cramming',
      '3,000 words = up to 95–98% speech comprehension',
      'Start speaking during the learning process',
      'From A1 to B2+ in ~6 months',
    ],
  },
  de: {
    title: `${SITE_CONFIG.brandName} — die moderne Art, eine Sprache schneller zu lernen:`,
    items: [
      'Keine tausenden € für Nachhilfe',
      'Spielerisches Lernen statt Pauken',
      '3.000 Wörter = bis zu 95–98% Sprachverständnis',
      'Sprich schon während des Lernens',
      'Von A1 bis B2+ in ca. 6 Monaten',
    ],
  },
};

function WelcomeBlock({ userId, onSettingsSaved }) {
  const { language, isBrowser, isTelegramWebApp } = useAppConfig();
  const { isGuest } = useAuth();
  const isNative = Capacitor.isNativePlatform();

  const uiLang = normalizeLang(language);
  const content = WELCOME_CONTENT[uiLang] || WELCOME_CONTENT.ru;

  // track only in normal browser guest flow
  const trackWelcome = !!(isGuest && isBrowser && !isNative && !isTelegramWebApp);
  const { flush } = useWelcomeTimeAnalytics({ trackWelcome, userId });

  const handleSaved = (payload) => {
    flush(true);
    onSettingsSaved?.(payload);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.welcomeBlock}>
        <div className={styles.content}>
          <div className={styles.badge}>AI language learning</div>
          <h1 className={styles.title}>{content.title}</h1>
          <ul className={styles.list}>
            {content.items.map((item, index) => (
              <li key={index} className={styles.listItem}>
                <span className={styles.check}>✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.settingsWrap}>
          <Settings userId={userId} onSaved={handleSaved} welkom={true} />
        </div>
      </div>
    </div>
  );
}

export default WelcomeBlock;